/**
 * Tunisian Chèque & Traite PDF template.
 *
 * Classic Tunisian format:
 *  - Traite (lettre de change): drawer info, drawee, amount (digits + letters),
 *    due date, place of payment, acceptance zone.
 *  - Chèque: simplified layout (number, bank, amount, dates).
 *
 * Uses pdfmake — same dependency as the rest of libs/pdf.
 */
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import type { TDocumentDefinitions, Content, Style } from 'pdfmake/interfaces';
import type { PdfCompanyInfo } from './types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(pdfMake as any).vfs = (pdfFonts as any).vfs;

// ---- Public types ----

export interface CheckDraftPdfData {
  /** 'check' | 'draft' */
  type: 'check' | 'draft';
  direction: 'received' | 'issued';
  number: string;
  amount: number;
  issueDate: string;
  dueDate: string;
  acceptanceDate?: string | null;
  bankName?: string | null;
  beneficiaryName?: string | null;
  placeOfPayment?: string | null;
  placeOfIssue?: string | null;
  thirdPartyName: string;
  notes?: string | null;
}

// ---- Helpers ----

function fmt(n: number): string {
  return n.toLocaleString('fr-TN', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '————————';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

/**
 * Minimal amount-in-letters for Tunisian DT (Dinar / Millimes).
 * Covers 0–999 999 DT. For larger amounts use a full library.
 */
function amountInWords(amount: number): string {
  const ones = ['', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf',
    'dix', 'onze', 'douze', 'treize', 'quatorze', 'quinze', 'seize',
    'dix-sept', 'dix-huit', 'dix-neuf'];
  const tens = ['', '', 'vingt', 'trente', 'quarante', 'cinquante', 'soixante', 'soixante-dix',
    'quatre-vingt', 'quatre-vingt-dix'];

  function belowThousand(n: number): string {
    if (n === 0) return '';
    if (n < 20) return ones[n] ?? '';
    const ten = Math.floor(n / 10);
    const one = n % 10;
    if (ten === 7 || ten === 9) {
      return `${tens[ten]}-${ones[10 + one] ?? ''}`;
    }
    const sep = (ten === 8 && one === 0) ? '' : one === 0 ? '' : '-';
    return `${tens[ten]}${sep}${ones[one] ?? ''}`.replace(/-$/, '');
  }

  const dinars = Math.floor(amount);
  const millimes = Math.round((amount - dinars) * 1000);

  let dinarsStr = '';
  const millions = Math.floor(dinars / 1_000_000);
  const thousands = Math.floor((dinars % 1_000_000) / 1000);
  const hundreds = Math.floor(dinars % 1000 / 100);
  const remainder = dinars % 100;

  if (millions > 0) dinarsStr += `${belowThousand(millions)} million${millions > 1 ? 's' : ''} `;
  if (thousands > 0) dinarsStr += `${belowThousand(thousands)} mille `;
  if (hundreds > 0) dinarsStr += `${ones[hundreds]} cent `;
  if (remainder > 0) dinarsStr += belowThousand(remainder);
  if (!dinarsStr.trim()) dinarsStr = 'zéro';

  let result = `${dinarsStr.trim()} dinar${dinars > 1 ? 's' : ''}`;
  if (millimes > 0) result += ` et ${belowThousand(millimes)} millime${millimes > 1 ? 's' : ''}`;
  return result;
}

// ---- PDF builder ----

export function generateCheckDraftPdf(
  company: PdfCompanyInfo,
  data: CheckDraftPdfData,
  action: 'print' | 'download' | 'open' = 'download',
): void {
  const isDraft = data.type === 'draft';

  const styles: Record<string, Style> = {
    title:    { fontSize: 14, bold: true, alignment: 'center' },
    label:    { fontSize: 8, color: '#666' },
    value:    { fontSize: 11, bold: true },
    amountWords: { fontSize: 10, italics: true, color: '#333' },
    footer:   { fontSize: 8, color: '#888', alignment: 'center' },
  };

  const borderLine = (width = 1): Content =>
    ({ canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: width }] });

  const labelValue = (label: string, value: string): Content => ({
    stack: [
      { text: label, style: 'label' },
      { text: value, style: 'value' },
    ],
    margin: [0, 0, 0, 8] as [number, number, number, number],
  });

  const header: Content[] = [
    { text: company.companyName, style: 'title', margin: [0, 0, 0, 4] as [number, number, number, number] },
    ...(company.taxId ? [{ text: `MF: ${company.taxId}`, style: 'label' as const, alignment: 'center' as const }] : []),
    ...(company.address ? [{ text: company.address, style: 'label' as const, alignment: 'center' as const }] : []),
    { text: isDraft ? 'LETTRE DE CHANGE' : 'CHÈQUE', style: 'title' as const, margin: [0, 12, 0, 4] as [number, number, number, number] },
    borderLine(1.5),
  ];

  const amountBlock: Content = {
    columns: [
      {
        width: '*',
        stack: [
          labelValue('Montant (chiffres)', `${fmt(data.amount)} DT`),
          labelValue('Montant (lettres)', amountInWords(data.amount)),
        ],
      },
    ],
    margin: [0, 12, 0, 4] as [number, number, number, number],
  };

  const datesBlock: Content = {
    columns: [
      labelValue("Date d'émission", fmtDate(data.issueDate)),
      labelValue("Date d'échéance", fmtDate(data.dueDate)),
      ...(isDraft && data.acceptanceDate
        ? [labelValue("Date d'acceptation", fmtDate(data.acceptanceDate))]
        : []),
    ],
    columnGap: 20,
    margin: [0, 0, 0, 4] as [number, number, number, number],
  };

  const partiesBlock: Content = {
    columns: [
      labelValue(
        data.direction === 'issued' ? 'Bénéficiaire / Payez à l\'ordre de' : 'Tireur',
        data.beneficiaryName || data.thirdPartyName,
      ),
      labelValue(
        data.direction === 'issued' ? 'Tiré (notre compte)' : 'Bénéficiaire',
        company.companyName,
      ),
    ],
    columnGap: 20,
    margin: [0, 0, 0, 4] as [number, number, number, number],
  };

  const bankBlock: Content = {
    columns: [
      labelValue('Banque', data.bankName || '————————'),
      labelValue('N° chèque/traite', data.number),
      ...(data.placeOfPayment
        ? [labelValue('Lieu de paiement', data.placeOfPayment)]
        : []),
    ],
    columnGap: 20,
    margin: [0, 0, 0, 4] as [number, number, number, number],
  };

  // Acceptance zone (traite only)
  const acceptanceZone: Content[] = isDraft
    ? [
        borderLine(),
        {
          text: 'ZONE D\'ACCEPTATION',
          style: 'label',
          margin: [0, 8, 0, 4] as [number, number, number, number],
        },
        {
          columns: [
            {
              stack: [
                { text: 'Signature du tiré :', style: 'label' },
                { canvas: [{ type: 'rect', x: 0, y: 0, w: 200, h: 40, lineWidth: 0.5 }] },
              ],
            },
            {
              stack: [
                { text: 'Date d\'acceptation :', style: 'label' },
                { text: fmtDate(data.acceptanceDate), style: 'value' },
              ],
            },
          ],
          columnGap: 20,
          margin: [0, 0, 0, 8] as [number, number, number, number],
        },
      ]
    : [];

  const notesBlock: Content[] = data.notes
    ? [
        borderLine(0.5),
        { text: 'Notes', style: 'label', margin: [0, 4, 0, 2] as [number, number, number, number] },
        { text: data.notes, fontSize: 9 },
      ]
    : [];

  const docDef: TDocumentDefinitions = {
    pageSize: 'A4',
    pageMargins: [40, 40, 40, 40],
    content: [
      ...header,
      amountBlock,
      borderLine(0.5),
      datesBlock,
      partiesBlock,
      bankBlock,
      ...acceptanceZone,
      ...notesBlock,
      borderLine(),
      {
        text: `Référence: ${data.number} — ${fmtDate(data.issueDate)}`,
        style: 'footer',
        margin: [0, 8, 0, 0] as [number, number, number, number],
      },
    ],
    defaultStyle: { font: 'Roboto', fontSize: 10 },
    styles,
  };

  const pdf = pdfMake.createPdf(docDef);
  const filename = `${data.type === 'draft' ? 'traite' : 'cheque'}-${data.number}.pdf`;
  if (action === 'download') {
    pdf.download(filename);
  } else if (action === 'open') {
    pdf.open();
  } else {
    pdf.print();
  }
}
